import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, FileText } from 'lucide-react';
import { formsApi } from '../../services/api';
import { showError } from '../../utils/toast';
import LoadingCard from '../../components/LoadingCard';
import { useTheme } from '../../context/ThemeContext';

// PDF Generation Libraries
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/** Format ISO date string to readable date & time */
const formatDate = (dateStr: string): string => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

/** Check if a value is a base64 data URL (image) */
const isBase64Image = (value: any): boolean => {
  if (typeof value !== 'string') return false;
  return value.startsWith('data:image/');
};

/** Check if a value looks like an image URL */
const isImageUrl = (value: any): boolean => {
  if (typeof value !== 'string') return false;
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(value) || value.startsWith('data:image/');
};

/** Check if a value is a file/image object returned by the API { url, name, ... } */
const isFileObject = (value: any): boolean =>
  value !== null && typeof value === 'object' && !Array.isArray(value) && typeof value.url === 'string';

/** Render a submission field value with proper image rendering */
const RenderFieldValue: React.FC<{ fieldKey: string; value: any }> = ({ fieldKey, value }) => {
  // API file/signature object: { url, name, size, type, path }
  if (isFileObject(value)) {
    const isImage = value.type?.startsWith('image/') || isImageUrl(value.url);
    return (
      <div className="mt-1 space-y-1">
        {isImage ? (
          <a href={value.url} target="_blank" rel="noopener noreferrer">
            <img
              src={value.url}
              alt={value.name || fieldKey}
              className="max-w-xs max-h-48 rounded-lg border border-gray-200 shadow-sm object-contain cursor-pointer hover:opacity-90 transition-opacity"
            />
          </a>
        ) : (
          <a
            href={value.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 underline text-sm"
          >
            <FileText className="w-4 h-4" />
            {value.name || 'Download file'}
          </a>
        )}
        {value.name && <p className="text-xs text-gray-400">{value.name}{value.size ? ` (${(value.size / 1024).toFixed(1)} KB)` : ''}</p>}
      </div>
    );
  }

  if (isBase64Image(value) || isImageUrl(value)) {
    return (
      <div className="mt-1">
        <img
          src={value}
          alt={fieldKey}
          className="max-w-xs max-h-48 rounded-lg border border-gray-200 shadow-sm object-contain"
          crossOrigin="anonymous"
        />
      </div>
    );
  }

  if (Array.isArray(value) && value.length > 0 && value.every((v) => isBase64Image(v) || isImageUrl(v))) {
    return (
      <div className="flex flex-wrap gap-3 mt-1">
        {value.map((img, idx) => (
          <img
            key={idx}
            src={img}
            alt={`${fieldKey}-${idx}`}
            className="max-w-[120px] max-h-32 rounded-lg border border-gray-200 shadow-sm object-contain"
            crossOrigin="anonymous"
          />
        ))}
      </div>
    );
  }

  if (Array.isArray(value)) {
    return <span>{value.join(', ')}</span>;
  }

  return <span>{String(value)}</span>;
};

const FormSubmissionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  useTheme(); // ensures component re-renders on theme change
  const formId = id ? parseInt(id) : null;

  // Ref to capture the content for PDF
  const pdfRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState<any>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (formId) fetchData();
  }, [formId]);

  const fetchData = async () => {
    if (!formId) return;
    setLoading(true);
    try {
      const [formData, submissionsData] = await Promise.all([
        formsApi.getById(formId),
        formsApi.getSubmissions(formId, { page: 1, limit: 50 })
      ]);
      setForm(formData);
      setSubmissions(submissionsData.data);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const viewSubmission = async (submissionId: number) => {
    if (!formId) return;
    try {
      const data = await formsApi.getSubmissionById(formId, submissionId);
      setSelectedSubmission(data);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to fetch submission');
    }
  };

  /** * Multi-page PDF Logic
   * This captures the element and slices it into multiple A4 pages if it's long.
   */
  const handleDownloadPDF = async () => {
    if (!pdfRef.current) return;
    
    setExporting(true);
    try {
      const element = pdfRef.current;
      // Temporarily force white background for PDF capture
      element.style.backgroundColor = '#ffffff';
      element.style.color = '#111111';
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });
      // Restore
      element.style.backgroundColor = '';
      element.style.color = '';

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // First Page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Additional Pages if content is longer than one A4
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Submission_${selectedSubmission.id}_Full.pdf`);
    } catch (error) {
      console.error('PDF Error:', error);
      showError('Failed to generate multi-page PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <LoadingCard title="Form Submissions" message="Loading..." />;
  if (!form) return <div className="p-6">Form not found</div>;

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/form')}
            className="p-2 theme-text-secondary hover:theme-bg-secondary rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold theme-text-primary">{form.title}</h1>
            <p className="theme-text-secondary mt-1">{submissions.length} submissions</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="theme-card rounded-xl shadow-sm overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="theme-bg-secondary">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium theme-text-secondary uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium theme-text-secondary uppercase">Submitted At</th>
                <th className="px-6 py-3 text-left text-xs font-medium theme-text-secondary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y theme-divide">
              {submissions.map((submission) => (
                <tr key={submission.id} className="hover:theme-bg-secondary">
                  <td className="px-6 py-4 text-sm theme-text-primary">{submission.id}</td>
                  <td className="px-6 py-4 text-sm theme-text-secondary">
                    {formatDate(submission.created_at)}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => viewSubmission(submission.id)}
                      className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-hidden">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="theme-card rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col"
          >
            {/* Modal Header - Not in PDF */}
            <div className="p-4 border-b theme-divide flex justify-between items-center theme-bg-secondary rounded-t-xl">
              <h2 className="text-lg font-bold theme-text-primary">Submission Details</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleDownloadPDF}
                  disabled={exporting}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  {exporting ? 'Preparing Pages...' : 'Download Full PDF'}
                </button>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="px-4 py-2 theme-bg-tertiary theme-text-secondary rounded-lg hover:theme-bg-secondary"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Scrollable Container for Modal UI */}
            <div className="overflow-y-auto flex-1 theme-card">
              <div ref={pdfRef} className={`p-10 ${exporting ? 'bg-white' : ''}`}>
                <div className="border-b-4 border-blue-600 pb-4 mb-8">
                  <h1 className="text-3xl font-extrabold theme-text-primary">{form.title}</h1>
                  <div className="flex justify-between mt-4 text-sm theme-text-secondary">
                    <span>Submission ID: #{selectedSubmission.id}</span>
                    <span>Date: {formatDate(selectedSubmission.created_at)}</span>
                  </div>
                </div>

                <div className="space-y-8">
                  {Object.entries(selectedSubmission.data || {}).map(([key, value]) => (
                    <div key={key} className="page-break-inside-avoid">
                      <div className="text-xs font-black text-blue-500 uppercase tracking-widest mb-2">
                        {key.replace(/_/g, ' ')}
                      </div>
                      <div className="theme-text-primary leading-relaxed border-l-4 theme-divide pl-4">
                        <RenderFieldValue fieldKey={key} value={value} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-12 pt-4 border-t theme-divide text-[10px] theme-text-secondary text-center">
                  End of Report - Generated via Form Management System
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default FormSubmissionsPage;